import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useCallback, useMemo, useState } from 'react';

import { asksForKnowledge, orderStatuses } from '~/components/tasks/taskStyle';
import { tasksPartialUpdate } from '~/data/generated/endpoints/tasks/tasks';
import type { TaskDetail, TaskStatus, TaskSummary } from '~/data/generated/model';
import { useRevalidateTasks } from '~/data/hooks/taskKeys';

const KEYBOARD_CODES = { start: ['Space'], cancel: ['Escape'], end: ['Space'] };

export function useTaskDragSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { keyboardCodes: KEYBOARD_CODES }),
  );
}

function useMoveTask() {
  const revalidate = useRevalidateTasks();
  return useCallback(
    async (taskId: string, statusId: string) => {
      const { data } = await tasksPartialUpdate(taskId, { statusId });
      await revalidate();
      return data;
    },
    [revalidate],
  );
}

export type StatusGroup = {
  status: TaskStatus;
  shown: TaskSummary[];
};

type MoveChain = {
  latest: number;
  done: Promise<void>;
  /** The status the server last confirmed, which the next PATCH moves from. */
  confirmed: TaskStatus;
  /** The last move the server saved, with the status it moved from. */
  saved: { task: TaskDetail; from: TaskStatus } | null;
};

export function useStatusGroups(
  tasks: TaskSummary[],
  statuses: readonly TaskStatus[],
  onCompleted?: (task: TaskDetail) => void,
) {
  const moveTask = useMoveTask();
  const [pending, setPending] = useState<Record<string, TaskStatus>>({});
  const [moveFailed, setMoveFailed] = useState(false);
  const [chains] = useState(() => new Map<string, MoveChain>());

  const groups = useMemo<StatusGroup[]>(() => {
    const known = new Set(statuses.map((s) => s.id));
    const unlisted = new Map(
      tasks.filter((t) => !known.has(t.status.id)).map((t) => [t.status.id, t.status]),
    );
    return orderStatuses([...statuses, ...unlisted.values()]).map((status) => ({
      status,
      shown: tasks.filter((t) => (pending[t.id] ?? t.status).id === status.id),
    }));
  }, [statuses, tasks, pending]);

  // Moves of one card run one after another, so the server applies them in drag
  // order. Only the latest settles the card, shows the alert and opens the prompt;
  // an earlier move finishing mid-drag would otherwise act on a move already undone.
  // The prompt follows the last move the server saved, judged from the status the
  // server had before it, as the task page's status picker would see it.
  const move = async (taskId: string, statusId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    const to = groups.find((g) => g.status.id === statusId)?.status;
    if (!task || !to || (pending[taskId] ?? task.status).id === statusId) return;
    setMoveFailed(false);
    setPending((prev) => ({ ...prev, [taskId]: to }));

    const chain = chains.get(taskId) ?? {
      latest: 0,
      done: Promise.resolve(),
      confirmed: task.status,
      saved: null,
    };
    const seq = chain.latest + 1;
    chain.latest = seq;
    chain.done = chain.done
      .catch(() => undefined)
      .then(async () => {
        const from = chain.confirmed;
        const updated = await moveTask(taskId, statusId);
        chain.confirmed = updated.status;
        chain.saved = { task: updated, from };
      });
    chains.set(taskId, chain);

    let failed = false;
    try {
      await chain.done;
    } catch {
      failed = true;
    }
    if (chain.latest !== seq) return;
    chains.delete(taskId);
    if (failed) setMoveFailed(true);
    const { saved } = chain;
    if (saved && asksForKnowledge(saved.task, saved.from)) onCompleted?.(saved.task);
    setPending((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  return {
    groups,
    move,
    moveFailed,
    dismissMoveFailed: () => setMoveFailed(false),
  };
}
